import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/apiService';
import Button from '@msi/cobalt-react/button';
import Icon from '@msi/cobalt-react/icon';
import { useOktaAuth } from '@okta/okta-react';
import { Api } from '../../types/apiData';
import './ApiManagedByUser.css'


interface MappedData {
    apiName: string[];
    apiType: string[];
    apiByUserCounts: Map<string, number>;
}

const ApiManagedByUser: React.FC = () => {
    const navigate = useNavigate();
    const [apiByUserCounts, setapiByUserCounts] = React.useState<Map<string, number>>(new Map());
    const [apiName , setApiName] = React.useState<string[]>([]);
    const [apiType , setApiType] = React.useState<string[]>([]);
    const [countByType , setCountByType] = React.useState<Map<string, number>>(new Map());
    const { authState } = useOktaAuth();
    const [apiData, setApiData] = React.useState<any[]>([]);

    const extractData = (apiData: Api[]) => {
        console.log("Extracting data from:", apiData);
        
        //Add a property name and return the processed data
        const processedData = apiData.map((api) => {
            const apiName = api.fullApiName;
            const apiType = api.apiStructure;
            const managedByUser = api.userCanEditAndDelete ? 'true' : 'false'; // Assuming this is the condition to check if managed by user
            //console.log(`API ID: ${api.id}, API Name: ${apiName}, Api Type: ${apiType} Managed by user: ${managedByUser}`);
            return { apiName, managedByUser, apiType };
        });
        console.log("Extraction complete, processed data:", processedData);
        return processedData;
    }

    const mapData = (array: any[]): MappedData => {
        const apiName = new Array<string>();
        const apiType = new Array<string>();
        const countManagedByUser = new Map<string, number>();
        array.forEach(item => {
            if (item.managedByUser === 'true' && item.apiName) { // Only include if managed by user
                countManagedByUser.set(item.apiName, (countManagedByUser.get(item.apiName) || 0) + 1);
                apiName.push(item.apiName); // Add API name to the array
                if (item.apiType && item.apiType != 'undefined') { // Check if apiType exists
                    const type = item.apiType; // Use the full type string
                    apiType.push(type); // Add API type to the array
                } else {
                    item.apiType = 'Type Undefined'; // Set to 'undefined' if not present
                    apiType.push(item.apiType); // Add API type to the array
                }
                
            } else if (item.managedByUser.size === 0) {
                console.log("You do not manage any APIs");
            }
        });
        setApiName(apiName);
        setApiType(apiType);
        //console.log("API Names and Types:", apiName, apiType);
        //console.log ("Apis you manage:", apiByUserCounts);
        console.log("Count apis:", countManagedByUser);
        return { apiName, apiType, apiByUserCounts };
    }

    const countApi = (mappedData: MappedData) => {
        const countsApis = new Map<string, number>();
        const apiTypeCounts = new Map<string, number>();
            mappedData.apiType.forEach(type => {
                apiTypeCounts.set(type, (apiTypeCounts.get(type) || 0) + 1);
            })

            mappedData.apiName.forEach(name => {
                countsApis.set(name, (countsApis.get(name) || 0) + 1);
            });

        setapiByUserCounts(countsApis);
        setCountByType(apiTypeCounts);
        //console.log("Apis you manage:", apiByUserCounts);
        //console.log("Count:", countByType);
        return {countsApis, countByType };
    }


    const getData = async() => {
        try {
            const apiService = new ApiService(); // Create an instance
            const data = (await apiService.getAllApis(authState)) as any[];
            setApiData(data);
            
            //console.log("API data fetched:", data);
            const extractedData = extractData(data);
            //console.log("Extracted API data: ", extractedData);
            const apis = mapData(extractedData);
            //console.log("Mapped API data: ", apis);
            //console.log("API Names: ", apiName);
            //console.log("API Types: ", apiType);
            const counts = countApi(apis);
            //console.log("Count by type:", counts);
            //console.log("Processed API data: ", extractedData);
        } catch (error) {
            console.error("Error fetching API data:", error);
        }
    }

    // Click handlers for navigation to table view with filters
    const handleChartClick = () => {
        // Navigate to table view showing only APIs managed by current user
        const searchParams = new URLSearchParams();
        searchParams.set('managedBy', "Me");
        navigate(`/table-view?${searchParams.toString()}`);
    };

    const handleTypeClick = (apiType: string) => {
        // Navigate to table view with both managedBy and apiStructure filters
        const searchParams = new URLSearchParams();
        searchParams.set('managedBy', "Me");
        searchParams.set('apiStructure', apiType);
        navigate(`/table-view?${searchParams.toString()}`);
    };

    useEffect(() => {
        getData();
    }, [authState]);

    useEffect(() => {
        if (apiData.length > 0) {
           //extractApiName(apiData);
        }
    }, [apiByUserCounts]);

    // Helper function to get the count from Map for display
    const getCountFromMap = () => {
        return apiName.length || 0;
    }

    return (
        <div className="display-content">
            <div className="chart-content">
                <h2>You Manage</h2>
                {getCountFromMap() === 0 ? (
                    <div className="no-apis-message">
                        <h1>0 APIs</h1>
                        <Icon name='ic_error_secondary' size={55} color='grey'/>
                        <p className="no-apis">Seems you don't manage any APIs at the moment...</p>
                    </div>
                ) : (
                    <>
                        <div 
                            className="clickable-chart-section"
                            onClick={handleChartClick}
                            style={{ cursor: 'pointer' }}
                            title="Click to view all APIs you manage"
                        >
                            <h1>{getCountFromMap()} APIs</h1>
                        </div>
                        <div className="icons-container">
                            <div className="types">
                                {Array.from(countByType.entries()).map(([type, count]) => (
                                    <div 
                                        className="type clickable-type" 
                                        title={`Click to view ${type} APIs you manage`} 
                                        key={type}
                                        onClick={() => handleTypeClick(type)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="type-count">{count}</div>
                                        <div className="type-name">{type === "Type Undefined" ? type : type.split(' ')[0]}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ApiManagedByUser;